/* global Promise */
import {jsPDF} from 'jspdf';

const BRAND_PURPLE = [132, 94, 238];
const INK = [26, 29, 41];
const MUTED = [101, 107, 122];
const BORDER = [229, 231, 235];
const SURFACE = [247, 247, 250];
const PROVIDER_SAFE_TRANSACTION_TYPES = ['DEPOSIT', 'WITHDRAWAL'];

const statusPalette = {
  SUCCESSFUL: {accent: [17, 145, 95], background: [235, 249, 243]},
  PENDING: {accent: [190, 112, 0], background: [255, 247, 226]},
  FAILED: {accent: [200, 55, 55], background: [254, 239, 239]},
  CANCELLED: {accent: [200, 55, 55], background: [254, 239, 239]},
};

export const supportsProviderSafeReceipt = transaction =>
  PROVIDER_SAFE_TRANSACTION_TYPES.includes(transaction?.type);

const humanize = value => {
  if (value === undefined || value === null || value === '') return '-';
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());
};

const displayValue = value => {
  if (value === undefined || value === null || value === '') return '-';
  if (Array.isArray(value)) return value.filter(Boolean).join('; ') || '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const parseObject = value => {
  if (!value) return {};
  if (typeof value === 'object') return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return displayValue(value);

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(date);
};

const formatMoney = (value, currency) => {
  if (value === undefined || value === null || value === '') return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `${currency || ''} ${value}`.trim();

  return `${currency || ''} ${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
};

const getAmountLabel = (transaction, providerSafe) => {
  if (!providerSafe) return 'AMOUNT NET OF FEES';
  if (transaction?.type === 'DEPOSIT') return 'FULL DEPOSIT AMOUNT';
  if (transaction?.type === 'WITHDRAWAL') return 'FULL WITHDRAWAL AMOUNT';
  return 'FULL TRANSACTION AMOUNT';
};

const getGatewayMetadata = transaction => {
  const paymentGateway = parseObject(transaction?.paymentGateway);
  return {
    ...paymentGateway,
    ...parseObject(paymentGateway?.metadata),
  };
};

const findLastAction = (transaction, {includeClaim = true} = {}) => {
  const claimedBy = transaction?.claimedTransaction?.createdBy;
  const claimedByName = `${claimedBy?.firstName || ''} ${
    claimedBy?.lastName || ''
  }`.trim();
  const events = [
    {
      label: 'Transaction created',
      date: transaction?.createdAt,
    },
    {
      label: 'Transaction updated',
      date: transaction?.updatedAt,
    },
    ...(includeClaim
      ? [
          {
            label: `Transaction claimed${
              claimedByName || claimedBy?.email
                ? ` by ${claimedByName || claimedBy.email}`
                : ''
            }`,
            date: transaction?.claimedTransaction?.createdAt,
          },
        ]
      : []),
  ].filter(
    event => event.date && !Number.isNaN(new Date(event.date).getTime()),
  );

  if (!events.length) return '-';
  const latestEvent = events.sort(
    (first, second) => new Date(second.date) - new Date(first.date),
  )[0];
  return `${latestEvent.label} - ${formatDateTime(latestEvent.date)}`;
};

const getMetadataFields = transaction => {
  const paymentMetadata = parseObject(transaction?.paymentMetadata);
  const gatewayMetadata = getGatewayMetadata(transaction);
  const safeFields = [
    ['Gateway reference', gatewayMetadata?.reference],
    ['Provider reference', gatewayMetadata?.providerReference],
    ['Receipt number', gatewayMetadata?.receiptNumber],
    ['Channel', gatewayMetadata?.channel || paymentMetadata?.channel],
    [
      'Network / operator',
      gatewayMetadata?.network ||
        paymentMetadata?.network ||
        paymentMetadata?.operator,
    ],
    ['Bank', gatewayMetadata?.bankName || paymentMetadata?.bankName],
    [
      'Country',
      gatewayMetadata?.country ||
        paymentMetadata?.country ||
        paymentMetadata?.countryCode,
    ],
  ];

  return safeFields
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    )
    .map(([label, value]) => ({
      label,
      value: ['Channel', 'Network / operator'].includes(label)
        ? humanize(value)
        : displayValue(value),
    }));
};

export const buildTransactionReceiptModel = (
  transaction,
  {providerSafe = false} = {},
) => {
  const gatewayMetadata = getGatewayMetadata(transaction);
  const paymentMetadata = parseObject(transaction?.paymentMetadata);
  const customerName = `${transaction?.user?.firstName || ''} ${
    transaction?.user?.lastName || ''
  }`.trim();
  const claimedBy = transaction?.claimedTransaction?.createdBy;
  const claimedByName = `${claimedBy?.firstName || ''} ${
    claimedBy?.lastName || ''
  }`.trim();
  const providerId =
    gatewayMetadata?.transactionId ||
    gatewayMetadata?.providerTransactionId ||
    gatewayMetadata?.id;
  const paymentMethod =
    paymentMetadata?.paymentMethod || gatewayMetadata?.paymentMethod;

  const sections = [
    {
      title: 'Customer',
      fields: [
        {label: 'Customer name', value: customerName || '-'},
        {
          label: 'Email',
          value: transaction?.user?.email || gatewayMetadata?.email || '-',
        },
        ...(!providerSafe
          ? [
              {label: 'Username', value: transaction?.user?.username || '-'},
              {label: 'Daba customer ID', value: transaction?.user?.id || '-'},
            ]
          : []),
      ],
    },
    {
      title: 'Transaction details',
      fields: [
        {label: 'Transaction ID', value: transaction?.id || '-'},
        {label: 'Reference ID', value: transaction?.referenceId || '-'},
        {label: 'Provider ID', value: providerId || '-'},
        {label: 'Status', value: humanize(transaction?.status)},
        {label: 'Type', value: humanize(transaction?.type)},
        {label: 'Direction', value: humanize(transaction?.direction)},
        {
          label: 'Payment provider',
          value: humanize(transaction?.paymentProvider),
        },
        {label: 'Payment method', value: humanize(paymentMethod)},
        {label: 'Currency', value: transaction?.currency || '-'},
        {label: 'Locked transaction', value: displayValue(transaction?.locked)},
      ],
    },
    {
      title: providerSafe ? 'Transaction amount' : 'Amount breakdown',
      fields: providerSafe
        ? [
            {
              label: 'Full transaction amount',
              value: formatMoney(
                transaction?.totalTransactionAmountIncludingFees ??
                  transaction?.transactionAmountNetOfFees,
                transaction?.currency,
              ),
            },
          ]
        : [
            {
              label: 'Total transaction amount',
              value: formatMoney(
                transaction?.totalTransactionAmountIncludingFees,
                transaction?.currency,
              ),
            },
            {
              label: 'Amount net of fees',
              value: formatMoney(
                transaction?.transactionAmountNetOfFees,
                transaction?.currency,
              ),
            },
            {
              label: 'Transaction fees',
              value: formatMoney(
                transaction?.transactionFee,
                transaction?.currency,
              ),
            },
            ...(transaction?.order?.totalFee !== undefined &&
            transaction?.order?.totalFee !== null
              ? [
                  {
                    label: 'Order fee',
                    value: formatMoney(
                      transaction.order.totalFee,
                      transaction?.order?.assetCurrency ||
                        transaction?.currency,
                    ),
                  },
                ]
              : []),
          ],
    },
    {
      title: 'Processing history',
      fields: [
        {label: 'Created', value: formatDateTime(transaction?.createdAt)},
        {label: 'Last updated', value: formatDateTime(transaction?.updatedAt)},
        {
          label: 'Last action',
          value: findLastAction(transaction, {includeClaim: !providerSafe}),
        },
        ...(!providerSafe && transaction?.claimedTransaction?.id
          ? [
              {
                label: 'Handled by',
                value: claimedByName || claimedBy?.email || '-',
              },
              {
                label: 'Claimed at',
                value: formatDateTime(transaction.claimedTransaction.createdAt),
              },
            ]
          : []),
      ],
    },
  ];

  const additionalFields = [
    ...(transaction?.failureReasonCode
      ? [
          {
            label: 'Failure reason code',
            value: transaction.failureReasonCode,
          },
        ]
      : []),
    ...(transaction?.failureReasonMessages
      ? [
          {
            label: 'Failure reason',
            value: displayValue(transaction.failureReasonMessages),
          },
        ]
      : []),
    ...(transaction?.order
      ? [
          {label: 'Order type', value: humanize(transaction.order.type)},
          {
            label: 'Order direction',
            value: humanize(transaction.order.direction),
          },
          {label: 'Asset symbol', value: transaction.order.symbol || '-'},
          {
            label: 'Order price',
            value: formatMoney(
              transaction.order.price,
              transaction.order.assetCurrency || transaction?.currency,
            ),
          },
        ]
      : []),
    ...(transaction?.swap
      ? [
          {
            label: 'Swap route',
            value: `${transaction.swap?.sourceCurrency?.currency || '-'} to ${
              transaction.swap?.targetCurrency?.currency || '-'
            }`,
          },
          {
            label: 'Amount swapped',
            value: formatMoney(
              transaction.swap.amountSwapped,
              transaction.swap?.sourceCurrency?.currency,
            ),
          },
          {
            label: 'Amount received',
            value: formatMoney(
              transaction.swap.amountReceived,
              transaction.swap?.targetCurrency?.currency,
            ),
          },
          ...(!providerSafe
            ? [
                {
                  label: 'Swap fees',
                  value: formatMoney(
                    transaction.swap.calculatedFees,
                    transaction.swap?.sourceCurrency?.currency,
                  ),
                },
              ]
            : []),
        ]
      : []),
    ...getMetadataFields(transaction),
  ];

  if (additionalFields.length) {
    sections.push({title: 'Additional details', fields: additionalFields});
  }

  return {
    customerName: customerName || 'Daba customer',
    status: transaction?.status || 'UNKNOWN',
    amount: formatMoney(
      providerSafe
        ? transaction?.totalTransactionAmountIncludingFees ??
            transaction?.transactionAmountNetOfFees
        : transaction?.transactionAmountNetOfFees ??
            transaction?.totalTransactionAmountIncludingFees,
      transaction?.currency,
    ),
    amountLabel: getAmountLabel(transaction, providerSafe),
    referenceId: transaction?.referenceId || transaction?.id || '-',
    sections,
  };
};

export const loadDabaLogo = async () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const response = await fetch(`${publicUrl}/daba-logo.png`);
  if (!response.ok) throw new Error('Unable to load the Daba logo');
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const createTransactionReceiptDocument = (
  transaction,
  {logoDataUrl, providerSafe = false} = {},
) => {
  const document = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
  });
  const model = buildTransactionReceiptModel(transaction, {providerSafe});
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  let y = 18;

  const addPage = () => {
    document.addPage();
    y = 14;
    if (logoDataUrl) {
      document.addImage(logoDataUrl, 'PNG', margin, y, 28, 8.3);
    } else {
      document.setTextColor(...BRAND_PURPLE);
      document.setFont('helvetica', 'bold');
      document.setFontSize(18);
      document.text('daba', margin, y + 7);
    }
    document.setTextColor(...MUTED);
    document.setFont('helvetica', 'bold');
    document.setFontSize(7.5);
    document.text('TRANSACTION RECEIPT', pageWidth - margin, y + 3, {
      align: 'right',
    });
    document.setFont('helvetica', 'normal');
    document.text(model.referenceId, pageWidth - margin, y + 8, {
      align: 'right',
    });
    y += 15;
    document.setDrawColor(...BORDER);
    document.line(margin, y, pageWidth - margin, y);
    y += 9;
  };

  const ensureSpace = height => {
    if (y + height > pageHeight - 22) addPage();
  };

  if (logoDataUrl) {
    document.addImage(logoDataUrl, 'PNG', margin, y, 42, 12.5);
  } else {
    document.setTextColor(...BRAND_PURPLE);
    document.setFont('helvetica', 'bold');
    document.setFontSize(26);
    document.text('daba', margin, y + 9);
  }

  document.setTextColor(...MUTED);
  document.setFont('helvetica', 'bold');
  document.setFontSize(8.5);
  document.text('TRANSACTION RECEIPT', pageWidth - margin, y + 4, {
    align: 'right',
  });
  document.setFont('helvetica', 'normal');
  document.setFontSize(8);
  document.text(`Receipt ${model.referenceId}`, pageWidth - margin, y + 10, {
    align: 'right',
  });
  y += 20;
  document.setDrawColor(...BORDER);
  document.line(margin, y, pageWidth - margin, y);
  y += 8;

  const palette = statusPalette[model.status] || {
    accent: BRAND_PURPLE,
    background: [244, 240, 254],
  };
  document.setFillColor(...palette.background);
  document.roundedRect(margin, y, contentWidth, 34, 3, 3, 'F');
  document.setTextColor(...MUTED);
  document.setFont('helvetica', 'normal');
  document.setFontSize(8.5);
  document.text(model.amountLabel, margin + 8, y + 10);
  document.setTextColor(...INK);
  document.setFont('helvetica', 'bold');
  document.setFontSize(21);
  const amountWidth = document.getTextWidth(model.amount);
  if (amountWidth > 105) {
    document.setFontSize(Math.max(12, (21 * 105) / amountWidth));
  }
  document.text(model.amount, margin + 8, y + 23);

  document.setFillColor(...palette.accent);
  document.roundedRect(pageWidth - margin - 40, y + 9, 32, 11, 5.5, 5.5, 'F');
  document.setTextColor(255, 255, 255);
  document.setFontSize(7.5);
  document.text(
    humanize(model.status).toUpperCase(),
    pageWidth - margin - 24,
    y + 16,
    {
      align: 'center',
    },
  );
  y += 44;

  const drawSection = section => {
    const gap = 8;
    const columnWidth = (contentWidth - gap) / 2;
    const rows = [];
    for (let index = 0; index < section.fields.length; index += 2) {
      const fields = section.fields.slice(index, index + 2).map(field => ({
        ...field,
        lines: document.splitTextToSize(
          displayValue(field.value),
          columnWidth - 4,
        ),
      }));
      rows.push({
        fields,
        height: Math.max(
          14,
          ...fields.map(field => 7 + field.lines.length * 4.2),
        ),
      });
    }

    const drawSectionHeading = continued => {
      document.setTextColor(...INK);
      document.setFont('helvetica', 'bold');
      document.setFontSize(11);
      document.text(
        `${section.title}${continued ? ' (continued)' : ''}`,
        margin,
        y,
      );
      y += 6;
    };

    ensureSpace(18);
    drawSectionHeading(false);

    rows.forEach(row => {
      if (y + row.height + 2 > pageHeight - 22) {
        addPage();
        drawSectionHeading(true);
      }

      row.fields.forEach((field, fieldIndex) => {
        const x = margin + fieldIndex * (columnWidth + gap);
        document.setTextColor(...MUTED);
        document.setFont('helvetica', 'normal');
        document.setFontSize(7.5);
        document.text(field.label.toUpperCase(), x, y + 3);
        document.setTextColor(...INK);
        document.setFontSize(9.3);
        document.text(field.lines, x, y + 8);
      });

      y += row.height;
      document.setDrawColor(...BORDER);
      document.line(margin, y, pageWidth - margin, y);
      y += 3;
    });
    y += 5;
  };

  model.sections.forEach(drawSection);

  ensureSpace(27);
  document.setFillColor(...SURFACE);
  document.roundedRect(margin, y, contentWidth, 22, 2, 2, 'F');
  document.setTextColor(...INK);
  document.setFont('helvetica', 'bold');
  document.setFontSize(8.5);
  document.text('Official Daba record', margin + 6, y + 7);
  document.setTextColor(...MUTED);
  document.setFont('helvetica', 'normal');
  document.setFontSize(7.5);
  const statement = document.splitTextToSize(
    'This receipt confirms the transaction recorded on Daba.',
    contentWidth - 12,
  );
  document.text(statement, margin + 6, y + 13);

  const totalPages = document.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    document.setPage(page);
    document.setDrawColor(...BORDER);
    document.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
    document.setTextColor(...MUTED);
    document.setFont('helvetica', 'normal');
    document.setFontSize(7);
    document.text('Daba Finance - Transaction receipt', margin, pageHeight - 9);
    document.text(
      `Page ${page} of ${totalPages}`,
      pageWidth - margin,
      pageHeight - 9,
      {
        align: 'right',
      },
    );
  }

  return document;
};

const downloadReceipt = async (transaction, {providerSafe = false} = {}) => {
  let logoDataUrl;
  try {
    logoDataUrl = await loadDabaLogo();
  } catch (error) {
    logoDataUrl = null;
  }

  const document = createTransactionReceiptDocument(transaction, {
    logoDataUrl,
    providerSafe,
  });
  const receiptId =
    transaction?.referenceId || transaction?.id || 'transaction';
  const safeReceiptId = String(receiptId).replace(/[^a-zA-Z0-9_-]/g, '-');
  const filename = `daba-${
    providerSafe ? 'provider-safe-' : ''
  }transaction-receipt-${safeReceiptId}.pdf`;
  document.save(filename);
  return filename;
};

export const downloadTransactionReceipt = transaction =>
  downloadReceipt(transaction);

export const downloadProviderSafeTransactionReceipt = transaction =>
  supportsProviderSafeReceipt(transaction)
    ? downloadReceipt(transaction, {providerSafe: true})
    : Promise.reject(
        new Error(
          'Provider-safe receipts are only available for deposits and withdrawals',
        ),
      );
